<script lang="ts" setup>
import '@unocss/reset/tailwind.css'
import { onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router/composables'
import AppGlobalHeaderFlipnovel from '@/components/AppGlobalHeader.flipnovel.vue'
import AppInitialLoadingFlipnovel from '@/components/AppInitialLoading.flipnovel.vue'
import AudioPlayerPopup from '@/components/AudioPlayerPopup.vue'
import AudioPlayerMini from '@/components/AudioPlayerMini.vue'
import { useAudioPlayerStore, useToastStore } from '@/stores'
import { usePageTime, useTechAppHide, useTechHttpCost } from '@/composables/tracks'
import { resetExternalPaymentRedirectPending } from '@/composables/payment/composables/paymentUtils'
import { brand, theme } from '@/config/brands'
import { loadBrandComponent } from '@/utils/loadBrandConfig'
import { $getToken } from '@/helpers'

const route = useRoute()
const isFlipnovelFamily = ['flipnovel', 'storlyn', 'moonpage', 'novaris'].includes(brand)
const isGoldLoadingBrand = brand === 'aurenovel'
const aurenovelPreviewGold = '#FFB300'

// 修复 flipnovel 家族站点：body/html 被 overflow:hidden，实际滚动在 #app 上，
// Vue Router scrollBehavior 只滚 window，导致各页面共享滚动偏移。
// 切页时手动重置 #app 的 scrollTop，其他品牌不受影响（#app 不会滚动）。
watch(() => route.path, () => {
  if (!isFlipnovelFamily)
    return

  const app = document.getElementById('app')
  if (app)
    app.scrollTop = 0
})

const loading = ref(true)
const { performanceObserver } = useTechHttpCost()
const { primaryColor, bgGlobal } = theme

function hexToRgba(color: string, alpha: number) {
  const normalized = color.replace('#', '')
  if (normalized.length !== 6)
    return `rgba(255, 138, 76, ${alpha})`

  const r = Number.parseInt(normalized.slice(0, 2), 16)
  const g = Number.parseInt(normalized.slice(2, 4), 16)
  const b = Number.parseInt(normalized.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

// 只有 flipnovel 家族品牌才需要自定义 Loading 和 Header
const AppInitialLoading = isFlipnovelFamily
  ? loadBrandComponent({
    flipnovel: AppInitialLoadingFlipnovel,
    moonpage: AppInitialLoadingFlipnovel,
    storlyn: AppInitialLoadingFlipnovel,
    novaris: AppInitialLoadingFlipnovel,
  })
  : null
const AppGlobalHeader = isFlipnovelFamily
  ? loadBrandComponent({
    flipnovel: AppGlobalHeaderFlipnovel,
    moonpage: AppGlobalHeaderFlipnovel,
    storlyn: AppGlobalHeaderFlipnovel,
    novaris: AppGlobalHeaderFlipnovel,
  })
  : null

function initApp() {
  performanceObserver()
  useTechAppHide()

  // 设置全局品牌主题 CSS 变量
  document.documentElement.style.setProperty('--primary-color', primaryColor)

  // 设置按钮背景（支持渐变或单色回退）
  const btnBackground = theme.primaryGradient || primaryColor
  document.documentElement.style.setProperty('--btn-primary-bg', btnBackground)

  // 需要 CSS ring loading 的品牌设置 spinner 相关 CSS 变量
  if (isFlipnovelFamily || isGoldLoadingBrand) {
    const loadingColor = isGoldLoadingBrand ? aurenovelPreviewGold : primaryColor
    document.documentElement.style.setProperty('--loading-spinner-color', loadingColor)
    document.documentElement.style.setProperty('--loading-spinner-border-color', hexToRgba(loadingColor, 0.26))
  }

  if (isGoldLoadingBrand)
    document.documentElement.style.setProperty('--loading-panel-bg', 'rgba(255, 249, 232, 0.96)')
}

onMounted(async () => {
  // eslint-disable-next-line no-console
  console.log('[App] => onMounted')
  loading.value = false

  usePageTime()

  // 外部支付页会冻结当前 H5 页面；主动清理提示，避免从 BFCache 返回时展示过期错误。
  window.addEventListener('pagehide', () => useToastStore().$clearToast())
  // 从外部支付页通过浏览器返回时，页面可能由 BFCache 恢复；复位跳转期间的请求错误抑制。
  window.addEventListener('pageshow', () => resetExternalPaymentRedirectPending())

  // 恢复音频播放器状态
  const audioStore = useAudioPlayerStore()
  const restored = audioStore.restoreState()
  const isPayingRoute = route.path === '/paying'
  // eslint-disable-next-line no-console
  console.log('[App] audio restoreState:', restored, 'payment_status=', route.query.payment_status, 'paymentRequiredChapterId=', audioStore.paymentRequiredChapterId, 'route=', route.path)
  if (restored && !isPayingRoute) {
    // token 不存在（如清除站点数据后 sessionStorage 残留旧状态），强制清理
    if (!$getToken()) {
      // eslint-disable-next-line no-console
      console.log('[App] no token found, closing audio player and clearing stale state')
      audioStore.closePlayer()
      return
    }
    audioStore.miniMode = true
    audioStore.visible = false
    const isPaymentReturn = route.query.payment_status === 'success'
    if (audioStore.paymentRequiredChapterId && (isPaymentReturn || !audioStore.chapterList.find(ch => ch.id === audioStore.paymentRequiredChapterId)?.ttsAudioUrl)) {
      // eslint-disable-next-line no-console
      console.log('[App] payment return detected, restoring popup and resuming play')
      audioStore.visible = true
      audioStore.miniMode = false
      await audioStore.resumePlayAfterPayment()
    }
    else if (isPaymentReturn) {
      // eslint-disable-next-line no-console
      console.log('[App] payment return but no pending chapter, just show mini-player')
    }
    // 清理 URL 参数
    {
      const url = new URL(window.location.href)
      if (isPaymentReturn)
        url.searchParams.delete('payment_status')
      if (url.searchParams.has('recommend') || url.searchParams.has('recommendChapterId')) {
        // eslint-disable-next-line no-console
        console.log('[App] cleaning up stale recommend params (set by audio payment flow)')
        url.searchParams.delete('recommend')
        url.searchParams.delete('recommendChapterId')
        window.history.replaceState({}, '', url.href)
      }
    }
  }
  else if (isPayingRoute) {
    // eslint-disable-next-line no-console
    console.log('[App] on /paying route, skip audio restore')
  }
  else {
    // eslint-disable-next-line no-console
    console.log('[App] no audio state to restore')
  }
})
initApp()
</script>

<template>
  <div id="app" :class="isFlipnovelFamily ? `brand-${brand}` : ''">
    <div v-if="loading">
      <template v-if="isFlipnovelFamily">
        <component :is="AppInitialLoading" />
      </template>
      <template v-else>
        <div class="pt-20vh flex justify-center">
          <BasicSpinner class="mx-auto" />
        </div>
      </template>
    </div>

    <div v-else>
      <component :is="AppGlobalHeader" v-if="isFlipnovelFamily" />
      <keep-alive>
        <router-view v-if="$route.meta?.keepAlive" />
      </keep-alive>
      <router-view v-if="!$route.meta?.keepAlive" />

      <BasicLoading />
      <BasicToast class="z-1001" />

      <AudioPlayerPopup />
      <AudioPlayerMini />
    </div>

    <portal-target name="root" multiple />
  </div>
</template>

<style>
/* PC大屏幕适配 CSS 变量 */
:root {
  --app-max-width: 750px;
  --app-content-left: 0px;
}

@media (min-width: 768px) {
  :root {
    --app-content-left: calc((100vw - var(--app-max-width)) / 2);
  }
}

html, body {
  height: 100%;
  -webkit-overflow-scrolling: auto;
  overflow: hidden;
}
body {
  font-family: -apple-system, BlinkMacSystemFont, SF UI Text, Helvetica Neue, PingFang SC, Hiragino Sans GB, Microsoft YaHei UI, Microsoft YaHei, Arial, sans-serif;
  touch-action: pan-y;
  font-size: 16px;
  max-width: var(--var-max-body-width, 750px);
  margin: 0 auto;
  background-color: v-bind(bgGlobal);
}
#app {
  height: 100%;
  margin: 0 auto;
  background: v-bind(bgGlobal);
  position: relative;
  overflow-y: scroll;
}

/** Some global transition  */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* slide-left */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.4s ease;
}
.slide-left-enter, .slide-left-leave-to {
  transform: translateX(100%);
}
/* slide-right */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.4s ease;
}
.slide-right-enter, .slide-right-leave-to {
  transform: translateX(-100%);
}

/** Some reset css */
p {
  margin: 0;
  overflow-wrap: anywhere;
}
div, input, textarea, button, select, a {
  -webkit-tap-highlight-color: transparent!important;
}
input:focus {
  outline: 0;
}
::-webkit-scrollbar {
  display: none;
}
.box-shadow {
  box-shadow: 0 2px 10px 0 rgba(220, 224, 228, 0.40);
}
</style>
